'use client';

import { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';

import { ReportEmptyState } from '@/app/reports/components/ReportEmptyState';
import { buildReportEChartOption } from '@/app/reports/components/report-echart-options';
import { echarts } from '@/app/reports/components/report-echarts';
import { toChartData } from '@/app/reports/components/report-chart-utils';
import { useReportChartThemeKey } from '@/app/reports/hooks/useReportChartTheme';
import type { ReportChartKind } from '@/app/reports/components/ReportChart';
import type { ReportSeriesPoint } from '@/app/reports/components/report-chart-utils';

type ReportEChartProps = {
  kind: ReportChartKind;
  series: ReportSeriesPoint[];
  currency?: string;
  isPercentage?: boolean;
  valueLabel: string;
  compact?: boolean;
};

const chartHeight = (kind: ReportChartKind, barCount: number, compact?: boolean) => {
  if (compact) return 96;
  if (kind === 'horizontal_bar' || kind === 'funnel') {
    return Math.min(680, Math.max(360, barCount * 30 + 72));
  }
  return 420;
};

export function ReportEChart({
  kind,
  series,
  currency,
  isPercentage,
  valueLabel,
  compact,
}: ReportEChartProps) {
  const themeKey = useReportChartThemeKey();
  const barCount = useMemo(() => toChartData(series).length, [series]);
  const hasData = barCount > 0;
  const height = chartHeight(kind, barCount, compact);
  const option = useMemo(
    () => buildReportEChartOption({ kind, series, currency, isPercentage, valueLabel }),
    [kind, series, currency, isPercentage, valueLabel, themeKey],
  );

  if (!hasData) return <ReportEmptyState variant="chart" minHeight={height} />;

  return (
    <div className="report-echarts-root w-full" style={{ height, minHeight: height }}>
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        notMerge={false}
        lazyUpdate={false}
        autoResize
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}
